import { Check, Eye, Pencil, Trash2, Users } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';

/** One view as the menu lists it. */
export interface GridViewMenuItem {
  id: string;
  name: string;
  /** Listed for the whole organisation. */
  shared: boolean;
  /** Whether this user may rename or delete it (and update it). */
  canEdit: boolean;
}

export interface GridViewMenuProps {
  views: GridViewMenuItem[];
  selectedId: string | null;
  /** The screen has changed since the selected view was picked or saved. */
  dirty: boolean;
  /** Shows "Share with everyone" when saving a new view. */
  canShare: boolean;
  onSelect: (id: string) => void;
  /** Stop showing a view name; the screen stays as it is. */
  onClear: () => void;
  /** Resolve true when saved (the form then closes). */
  onSaveNew: (name: string, shared: boolean) => Promise<boolean>;
  onUpdate: () => Promise<boolean>;
  onRename: (id: string, name: string) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  busy?: boolean;
  error?: string | null;
  className?: string;
}

const MAX_NAME = 60;

/**
 * The "Views" button on a grid's toolbar: named snapshots of the columns and
 * the screen's filters and sort. Picking one puts it on screen; changing
 * anything afterwards marks it "modified", with Update and Save as new. My
 * views are private; shared ones are for the whole organisation and only
 * people who can edit the organisation create, change or delete them.
 */
export function GridViewMenu({
  views,
  selectedId,
  dirty,
  canShare,
  onSelect,
  onClear,
  onSaveNew,
  onUpdate,
  onRename,
  onDelete,
  busy = false,
  error,
  className,
}: GridViewMenuProps) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameTo, setRenameTo] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const close = () => {
    setOpen(false);
    setSaving(false);
    setRenamingId(null);
    setDeletingId(null);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = views.find((v) => v.id === selectedId) ?? null;
  const mine = views.filter((v) => !v.shared);
  const everyone = views.filter((v) => v.shared);

  const startSave = () => {
    setSaving(true);
    setName(selected && dirty ? `${selected.name} (copy)`.slice(0, MAX_NAME) : '');
    setShared(false);
  };

  const submitSave = async () => {
    if (!name.trim()) return;
    if (await onSaveNew(name, shared)) setSaving(false);
  };

  const submitRename = async () => {
    if (!renamingId || !renameTo.trim()) return;
    if (await onRename(renamingId, renameTo)) setRenamingId(null);
  };

  const row = (v: GridViewMenuItem): ReactNode => {
    if (renamingId === v.id) {
      return (
        <li key={v.id} className="flex items-center gap-1 px-2 py-1">
          <input
            autoFocus
            value={renameTo}
            maxLength={MAX_NAME}
            onChange={(e) => setRenameTo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitRename();
              if (e.key === 'Escape') {
                e.stopPropagation();
                setRenamingId(null);
              }
            }}
            className="min-w-0 flex-1 rounded border border-slate-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <button
            type="button"
            disabled={busy || !renameTo.trim()}
            onClick={() => void submitRename()}
            className="text-[11px] font-medium text-purple-600 disabled:text-slate-300"
          >
            Save
          </button>
        </li>
      );
    }
    if (deletingId === v.id) {
      return (
        <li key={v.id} className="flex items-center gap-2 px-3 py-1 text-[11px]">
          <span className="min-w-0 flex-1 truncate text-slate-600">Delete “{v.name}”?</span>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              if (await onDelete(v.id)) setDeletingId(null);
            }}
            className="font-medium text-rose-600 disabled:text-slate-300"
          >
            Delete
          </button>
          <button type="button" onClick={() => setDeletingId(null)} className="text-slate-500">
            Keep
          </button>
        </li>
      );
    }
    return (
      <li key={v.id} className="group flex items-center gap-1 px-2 hover:bg-slate-50">
        <button
          type="button"
          onClick={() => {
            onSelect(v.id);
            close();
          }}
          className="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left text-slate-700"
        >
          <Check
            className={cn('h-3 w-3 shrink-0', v.id === selectedId ? 'text-slate-700' : 'invisible')}
            aria-hidden="true"
          />
          <span className="truncate">{v.name}</span>
        </button>
        {v.canEdit && (
          <span className="flex shrink-0 gap-0.5 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100">
            <button
              type="button"
              title="Rename"
              onClick={() => {
                setRenamingId(v.id);
                setRenameTo(v.name);
              }}
              className="rounded p-0.5 text-slate-400 hover:text-slate-700"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              type="button"
              title="Delete"
              onClick={() => setDeletingId(v.id)}
              className="rounded p-0.5 text-slate-400 hover:text-rose-600"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        )}
      </li>
    );
  };

  return (
    <div ref={boxRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Saved views: columns, filters and sort"
        className="flex max-w-[260px] items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
      >
        <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{selected ? selected.name : 'Views'}</span>
        {selected?.shared && (
          <Users className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" />
        )}
        {selected && dirty && (
          <span className="shrink-0 text-[10px] font-normal italic text-amber-600">modified</span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Views"
          className="absolute right-0 top-full z-40 mt-1 w-72 rounded-md border border-slate-200 bg-white py-2 text-xs shadow-lg"
        >
          {/* What can be done with what's on screen. */}
          <div className="space-y-1 px-3 pb-2">
            {selected && dirty && selected.canEdit && (
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  if (await onUpdate()) close();
                }}
                className="block text-[11px] font-medium text-purple-600 underline-offset-2 hover:underline disabled:text-slate-300"
              >
                Update “{selected.name}”
              </button>
            )}
            {!saving ? (
              <button
                type="button"
                disabled={busy}
                onClick={startSave}
                className="block text-[11px] font-medium text-purple-600 underline-offset-2 hover:underline disabled:text-slate-300"
              >
                Save as new view…
              </button>
            ) : (
              <div className="space-y-1.5 rounded border border-slate-200 bg-slate-50 p-2">
                <input
                  autoFocus
                  placeholder="View name, e.g. Open IE → GB"
                  value={name}
                  maxLength={MAX_NAME}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void submitSave();
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setSaving(false);
                    }
                  }}
                  className="w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                {canShare && (
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                    <input
                      type="checkbox"
                      checked={shared}
                      onChange={(e) => setShared(e.target.checked)}
                      className="h-3.5 w-3.5 accent-slate-700"
                    />
                    Share with everyone in the organisation
                  </label>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setSaving(false)}
                    className="text-[11px] text-slate-500 hover:text-slate-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={busy || !name.trim()}
                    onClick={() => void submitSave()}
                    className="rounded bg-slate-700 px-2 py-0.5 text-[11px] font-medium text-white disabled:bg-slate-300"
                  >
                    Save view
                  </button>
                </div>
              </div>
            )}
            {selected && (
              <button
                type="button"
                onClick={() => {
                  onClear();
                  close();
                }}
                title="The screen stays as it is"
                className="block text-[11px] text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline"
              >
                Stop using “{selected.name}”
              </button>
            )}
          </div>

          <div className="max-h-72 overflow-auto border-t border-slate-100 pt-1.5">
            <p className="px-3 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              My views
            </p>
            {mine.length === 0 ? (
              <p className="px-3 pb-1 text-[11px] italic text-slate-400">None yet</p>
            ) : (
              <ul>{mine.map(row)}</ul>
            )}
            <p className="mt-1.5 px-3 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Shared views
            </p>
            {everyone.length === 0 ? (
              <p className="px-3 pb-1 text-[11px] italic text-slate-400">None yet</p>
            ) : (
              <ul>{everyone.map(row)}</ul>
            )}
          </div>

          {error && <p className="px-3 pt-1.5 text-[11px] text-rose-600">{error}</p>}
        </div>
      )}
    </div>
  );
}
